import {useGetUser} from '@/features/users/queries/useGetUser.ts'
import {raise} from '@utilities/helpers.ts'
import {useURLParsedParams} from '@hooks/useURLParsedParams.ts'
import {UserDetailsInformationPersonalData} from '@/features/users/components/user-details-information-personal-data/UserDetailsInformationPersonalData.tsx'
import {UserDetailsInformationJobData} from '@/features/users/components/user-details-information-job-data/UserDetailsInformationJobData'
import {UserDetailsInformationPrivacy} from '@/features/users/components/user-details-information-privacy/UserDetailsInformationPrivacy'
import {UserDetailsInformationProfessionalData} from '@/features/users/components/user-details-information-professional-data/UserDetailsInformationProfessionalData.tsx'
import {ScrollArea} from '@components/ui/scroll-area/ScrollArea.tsx'
import {Scrollbar} from '@components/ui/scrollbar/Scrollbar.tsx'
import {UserDetailsInformationContactsData} from '@/features/users/components/user-details-information-contacts-data/UserDetailsInformationContactsData'
import {UserDetailsInformationContractualData} from '@/features/users/components/user-details-information-contractual-data/UserDetailsInformationContractualData'
import {useRef} from 'react'
import {useTranslation} from '@/translations/i18n'
import {DetailsInformationWarningsBanner} from '@/components/commons/details-information-warnings-banner/DetailsInformationWarningBanner'
import {BannerAlert} from '@/components/commons/banner-alert/BannerAlert'
import {Divider} from '@/components/ui/divider/Divider'
import {useUserDetailsStore} from '@/features/users/store/userDetailsStore'
import {UserDetailsUrlParams} from '@/types'
import {ScrollProvider, useScrollContext} from '@/hooks/useScrollContext'
import {Helmet} from 'react-helmet'
import {UserExtended} from '@/features/users/types'

export const UserDetailsInformation = () => {
    const {t} = useTranslation()
    const urlParams = useURLParsedParams(UserDetailsUrlParams)
    const userQuery = useGetUser({
        userId: urlParams.success ? urlParams.data.id : undefined,
        responseType: 'extended',
        options: {enabled: !!urlParams.success}
    })

    const user = userQuery.data ?? raise('user is nullish')

    return (
        <>
            <Helmet title={t('commons:fullname_details_meta_title', {fullName: user.fullName})} />
            <ScrollProvider>
                <UserDetailsInformationContent user={user} />
            </ScrollProvider>
        </>
    )
}

const UserDetailsInformationContent = ({user}: {user: UserExtended}) => {
    const isErrorBannerVisible = useUserDetailsStore(state => state.isErrorBannerVisible)
    const setIsErrorBannerVisible = useUserDetailsStore(state => state.setIsErrorBannerVisible)
    const errorBannerRef = useRef<HTMLDivElement>(null)
    const {scrollContainerRef} = useScrollContext()

    return (
        <ScrollArea scrollbar={<Scrollbar />} viewportProps={{ref: scrollContainerRef}}>
            {isErrorBannerVisible && (
                <>
                    <BannerAlert ref={errorBannerRef} onClose={() => setIsErrorBannerVisible(false)} />
                    <Divider direction="horizontal" />
                </>
            )}
            {(user.fieldsToCompile.length > 0 || user.fieldsToUpdate.length > 0) && (
                <DetailsInformationWarningsBanner
                    fieldsToCompile={user.fieldsToCompile}
                    fieldsToUpdate={user.fieldsToUpdate}
                />
            )}
            <UserDetailsInformationPersonalData user={user} />
            <UserDetailsInformationJobData user={user} />
            {user.specialization.name != 'Amministrativo' && <UserDetailsInformationProfessionalData user={user} />}
            <UserDetailsInformationContractualData user={user} />
            {user.contacts && user.contacts?.length > 0 && <UserDetailsInformationContactsData user={user} />}
            {user.contractType && <UserDetailsInformationPrivacy user={user} />}
        </ScrollArea>
    )
}
